import React from "react"
import { SortEnd } from "react-sortable-hoc"
import Form from "react-bootstrap/Form"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { LegalAttributeValue } from "../../../../../api/types"
import { Feedback } from "../../../../../components/Validator"
import SortableContainer from "../../../../../components/SortableContainer"
import Badge from "../../../../../components/Badge"
import Button from "../../../../../components/Button"

import { disableIfIntroducedInPreviousVersionOrOverride } from "../../../../../components/disableInputPolicy"
import CsvImport from "./CsvImport"
import CsvDownload from "./CsvDownload"

const valueId = "legalValueInputProperty"
const defaultValue = "legalValueSelectDefaultProperty"

interface ConfigureDropdownChoiceProps {
  fccId: string
  atId: number
  value: string
  default?: string;
  legalValues: LegalAttributeValue[]
  currentVersion: number
  refresh: () => void
  valid: Feedback
  disabled: boolean
  setDisabled: (value: boolean) => void
  onAdd: () => void
  onRemove: (id: number) => void
  onSortEnd: (e: SortEnd) => void
  setValue: (id: string, e: any) => void
  setDefault: (defaultValue: string, e: any) => void
}

const ConfigureDropdownChoice = (props: ConfigureDropdownChoiceProps) => {
  const disabledAllowedValueSort = props.disabled || !props.valid.valid
  const disabledCsvImportExport = disabledAllowedValueSort
  const disabledValue =
    props.disabled || (!props.valid.valid && props.valid.id !== valueId)
  const disabledAdd = props.disabled || !props.valid.valid || props.value === ""

  const inValidValue = props.valid.id === valueId

  const renderSortable = (item: LegalAttributeValue) => {
    const onRemove = () => props.onRemove(item.id!)

    const versionDisabled = disableIfIntroducedInPreviousVersionOrOverride(
      {
        introducedInVersion: item.introducedInVersion!,
      },
      props.currentVersion,
      false
    )

    return (
      <Badge
        className={disabledAllowedValueSort ? "m-1" : "m-1 cursor-move"}
        variant="secondary"
        // variant={item.primary ? "primary" : "secondary"}
        pill={true}
        outline={versionDisabled}
        disabled={disabledAllowedValueSort}
        rightIcon={
          versionDisabled ? (
            <i className="modus-icons">lock</i>
          ) : (
            <i className="modus-icons" onClick={onRemove}>
              circle_close
            </i>
          )
        }
        rightIconClassName={versionDisabled ? "" : "cursor-pointer"}
      >
        {item.value}
      </Badge>
    )
  }

  const renderDefaultOptions = () =>
    props.legalValues.map((lv, i) => {
      return lv.value ? (
        <option key={i} value={lv.id}>
          {lv.value}
        </option>
      ) : null
    })

  const setValue = (e: any) => props.setValue(valueId, e)
  const setDefault = (e: any) => props.setDefault(defaultValue, e)

  const onSubmit = (e: any) => {
    e.preventDefault()
    props.onAdd()
  }

  return (
    <Form onSubmit={onSubmit}>
      {props.legalValues && props.legalValues.length > 0 ? (
        <Form.Group className="mb-4">
          <SortableContainer
            className="border rounded p-2"
            items={props.legalValues}
            renderElement={renderSortable}
            disabled={disabledAllowedValueSort}
            axis="xy"
            pressDelay={100}
            helperClass="sortableHelper" // To fix z-index issue with modal
            onSortEnd={props.onSortEnd}
          />
          <Form.Text>
            Use drag-and-drop to rearrange the values. Remove an unlocked value
            by clicking the button.
          </Form.Text>
        </Form.Group>
      ) : null}

      <Form.Group as={Row} controlId={valueId}>
        <Form.Label column={true} md="3">
          Value
        </Form.Label>
        <Col md="9">
          <Form.Control
            type="text"
            value={props.value}
            onChange={setValue}
            disabled={disabledValue}
            isInvalid={inValidValue}
          />
          <Form.Control.Feedback type="invalid">
            {props.valid.message}
          </Form.Control.Feedback>
          <Form.Text>
            Replace all values with entries from a CSV file or add a single
            allowed value.
          </Form.Text>
        </Col>
      </Form.Group>

      <Form.Group as={Row} controlId={defaultValue}>
        <Form.Label column={true} md="3">
          Default Value
        </Form.Label>
        <Col md="9">
          <Form.Control
            as="select"
            // className='custom-select'
            // value={props.default}
            value={props.legalValues.find(v => v.value === props.default)?.id}
            onChange={setDefault}
            disabled={disabledAllowedValueSort || props.legalValues.length <= 0}
          >
            <option value="" disabled={true}>
              -- please select --
            </option>
            {renderDefaultOptions()}
          </Form.Control>
          <Form.Text>
            Set one of the allowed values as a default value for the property.
          </Form.Text>
        </Col>
      </Form.Group>

      <Form.Group className="d-flex">
        <CsvDownload
          fccId={props.fccId}
          atId={props.atId}
          refresh={props.refresh}
          disabled={disabledCsvImportExport}
          setDisabled={props.setDisabled}
          className="mr-2"
        >
          Download to CSV
        </CsvDownload>
        <CsvImport
          fccId={props.fccId}
          atId={props.atId}
          refresh={props.refresh}
          disabled={disabledCsvImportExport}
          setDisabled={props.setDisabled}
          className="mr-2"
        >
          Replace with CSV
        </CsvImport>
        <Button
          className="ml-auto"
          variant="primary"
          type="submit"
          disabled={disabledAdd}
        >
          Add allowed value
        </Button>
      </Form.Group>

    </Form>
  )
}

export default ConfigureDropdownChoice
